import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { FaLocationDot, FaUser, FaClock } from "react-icons/fa6";
import { IoSearch } from "react-icons/io5";
import { FaUserSlash } from "react-icons/fa";
import AppContext from "../../context/AppContext";
import { FiUser } from "react-icons/fi";
import { fetchFieldAgentMap, updateTaskDetails } from "../../service/Task";
import { toast } from "../../utils/toast";
import CommonButton from "../../components/common/CommonButton";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import ActionIconButton from "../../components/common/ActionIconButton";
import translations from "../../components/common/Translations";
import { getPermissionByScreenCode } from "../../utils/permissions";
import { filterAgentsByCurrentUserGroups } from "../../components/task/GroupAssignParts";
import { Pencil } from "lucide-react";

const getTaskStatusUiLabel = (langMode, statusKey, fallback = "") => {
  const tr = translations[langMode];
  if (!tr) return fallback || statusKey;
  switch (statusKey) {
    case "Active":
      return tr.statusActiveLabel ?? fallback;
    case "In Progress":
    case "In_Progress":
      return tr.statusInProgressLabel ?? fallback;
    case "Completed":
      return tr.statusCompletedLabel ?? fallback;
    case "Assigned":
      return tr.statusAssignedLabel ?? fallback;
    case "On_Hold":
      return tr.statusOnHoldLabel ?? fallback;
    default:
      return fallback || statusKey;
  }
};

const DetailPanel = ({
  selectedLocation,
  selectedTaskDetail,
  isTaskDetailLoading = false,
  onClose,
  onClear,
  onRefresh,
  allTasks = [], // Pass all tasks to calculate status counts
  onAgentClick,
  selectedAgentFilterId,
  selectedAgentId,
  selectedAgentName
}) => {
  const { activityId, langMode } = useContext(AppContext);
  const isToggleRtl = langMode === "ar" || langMode === "ur";
  const taskPerm = getPermissionByScreenCode("TASK_MANAGEMENT");
  const canUpdateTask = Boolean(taskPerm?.canUpdate);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState("");
  const [agentStatusFilter, setAgentStatusFilter] = useState("online"); // "online" | "offline" ? toggle between the two
  const [showTaskDetailEdit, setShowTaskDetailEdit] = useState(false);
  const [taskDetailFormData, setTaskDetailFormData] = useState({});
  const [taskDetailSaving, setTaskDetailSaving] = useState(false);

  // Fetch available agents
  useEffect(() => {
    const loadAgents = async () => {
      if (!activityId) return;
      setLoadingAgents(true);
      try {
        const response = await fetchFieldAgentMap(activityId);
        // API returns { body: { value: [...] } }; extract the agents array
        const rawAgents = Array.isArray(response?.body?.value)
          ? response.body.value
          : Array.isArray(response)
            ? response
            : [];
        // Only show field agents who belong to a group assigned to this user
        // (blank group list => show all).
        const agents = await filterAgentsByCurrentUserGroups(rawAgents);
        // Get agents with task counts (total assigned + completed)
        const agentsWithCounts = agents.map(agent => {
          const agentTasks = allTasks.filter(task =>
            task.assignees?.some(a =>
              a.userId === agent.userId ||
              a.name === agent.name ||
              a.userName === agent.userName
            )
          );

          return {
            ...agent,
            activeTaskCount: agentTasks.length,
            completedTaskCount: agent.completedTaskCount || 0
          };
        });
        setAvailableAgents(agentsWithCounts);
      } catch (error) {
        console.error("Error fetching agents:", error);
        setAvailableAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    };

    loadAgents();
  }, [activityId, allTasks]);

  const getSafeTaskDetails = (taskDetails) => {
    try {
      if (typeof taskDetails === "object" && taskDetails !== null) return taskDetails;
      if (typeof taskDetails === "string") return JSON.parse(taskDetails);
    } catch (error) {
      console.warn("Invalid taskDetails format:", taskDetails);
    }
    return {};
  };

  useEffect(() => {
    const parsedTaskDetails = selectedTaskDetail?.taskDetailsJson
      ? getSafeTaskDetails(selectedTaskDetail.taskDetailsJson)
      : selectedTaskDetail?.taskDetailsParsed || {};
    setTaskDetailFormData({
      priority: selectedTaskDetail?.priority || "",
      ...parsedTaskDetails,
    });
    setShowTaskDetailEdit(false);
  }, [selectedTaskDetail]);

  const handleTaskDetailInputChange = (e) => {
    const { name, value } = e.target;
    setTaskDetailFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateTaskDetail = async () => {
    if (!selectedTaskDetail?.taskId || taskDetailSaving) return;
    setTaskDetailSaving(true);
    const updatedTaskDetails = {
      ...(selectedTaskDetail.taskDetailsParsed || {}),
      ...taskDetailFormData,
    };
    const updatedTask = {
      taskId: selectedTaskDetail.taskId,
      workflowId: selectedTaskDetail.workflowId,
      assigneeId: selectedTaskDetail.assigneeId,
      priority: selectedTaskDetail.priority || "Low_Priority",
      taskDetailsJson: getSafeTaskDetails(updatedTaskDetails),
      taskStatus: selectedTaskDetail.taskStatus,
      assignedBy: selectedTaskDetail.assignedBy,
      createdDate: selectedTaskDetail.createdDate,
      latitude: selectedTaskDetail.longitude,
      longitude: selectedTaskDetail.latitude,
      areaCode: selectedTaskDetail.areaCode,
      tariff: selectedTaskDetail.tariff,
      accountNo: selectedTaskDetail.accountNo,
      createdBy: selectedTaskDetail.createdBy,
      finishedDate: selectedTaskDetail.finishedDate,
      office: selectedTaskDetail.office || null,
      area: selectedTaskDetail.area || null,
      source: selectedTaskDetail.source,
      icon: selectedTaskDetail.icon,
      completed: selectedTaskDetail.completed,
      assigned: selectedTaskDetail.assigned,
      deleted: selectedTaskDetail.deleted,
    };
    try {
      await updateTaskDetails(updatedTask);
      toast.success("Task details updated successfully");
      setShowTaskDetailEdit(false);
      onRefresh?.();
    } catch (error) {
      console.error("Error updating task details:", error);
      toast.error("Failed to update task details");
    } finally {
      setTaskDetailSaving(false);
    }
  };

  // Derive primary agent name from selected location tasks (fallback to selectedLocation.agent)
  const primaryAgentName = (() => {
    if (selectedLocation?.tasks?.length) {
      const taskWithAssignee = selectedLocation.tasks.find(
        (task) => Array.isArray(task.assignees) && task.assignees.length > 0
      );
      if (taskWithAssignee) {
        const assignee = taskWithAssignee.assignees[0];
        return (
          assignee.name ||
          assignee.userName ||
          assignee.firstName ||
          selectedLocation.agent
        );
      }
    }
    return selectedLocation?.agent;
  })();

  // Format coordinates
  const formatCoordinates = (lat, lng) => {
    const latDir = lat >= 0 ? "N" : "S";
    const lngDir = lng >= 0 ? "E" : "W";
    return `${Math.abs(lat).toFixed(4)}° ${latDir}, ${Math.abs(lng).toFixed(4)}° ${lngDir}`;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {

      case "Active":
        return "#4379F2"; // Blue
      case "In Progress":
      case "In_Progress":
        return "#FFA500"; // Orange
      case "Assigned":
        return "#F3C623"; // Yellow
      case "Completed":
        return "#6EC207"; // Green
      case "On_Hold":
        return "#FF3333"; // Red
      default:
        return "#A0A0A0";
    }
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Agent status: online (green dot) or office (gray dot). API sends boolean status: true = online, false = office
  const getAgentStatus = (agent) => {
    if (typeof agent?.status === "boolean") {
      return agent.status ? { label: "Online", isOnline: true } : { label: "Offline", isOnline: false };
    }
    const raw = agent?.userStatus ?? (agent?.isOnline === true ? "online" : agent?.isOnline === false ? "offline" : null);
    const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
    if (s === "online") return { label: "Online", isOnline: true };
    if (s === "offline") return { label: "Offline", isOnline: false };
    return { label: "?", isOnline: false };
  };

  return (
    <div
      className="w-full shadow-2xl border-l border-gray-200"
    >
      {/* Header */}
      <div className="pt-3 z-10">
        <div className="flex items-center border-b border-gray-200 justify-between pl-4 pr-3 pb-3">
          <h2 className="text-lg font-semibold text-gray-900">{translations[langMode].taskActions}</h2>
          <ModalCloseButton onClick={onClose} ariaLabel="Close" />
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Selected Location Section */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FaLocationDot className="text-blue-600" size={18} />
              <h3 className="text-base font-semibold text-gray-900">
                {selectedLocation?.isDrawnArea ? translations[langMode].drawnArea : translations[langMode].selectedLocation}
              </h3>
            </div>
            {selectedLocation && (
              <button
                onClick={onClear}
                className="text-sm text-red-600 hover:text-red-700 font-medium underline"
              >
                {translations[langMode].clearSelection}
              </button>
            )}
          </div>

          {selectedLocation ? (
            <>
              {/* LAT/LONG - show center for drawn area, or exact coords for single location */}
              {selectedLocation.latitude != null && selectedLocation.longitude != null && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <FaLocationDot className="text-blue-600" size={14} />
                    <span className="text-xs text-gray-500 uppercase">
                      {selectedLocation.isDrawnArea ? translations[langMode].centerApproxLabel : translations[langMode].latLongUpperLabel}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 font-medium">
                    {formatCoordinates(selectedLocation.latitude, selectedLocation.longitude)}
                  </p>
                </div>
              )}

              {/* AGENT */}
              {primaryAgentName && primaryAgentName !== "Unassigned" && (
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <FaUser className="text-blue-600" size={14} />
                    <span className="text-xs text-gray-500 uppercase">{translations[langMode].agentUpperLabel}</span>
                  </div>
                  <p className="text-sm text-gray-900 font-medium">{primaryAgentName}</p>
                </div>
              )}

              {/* ACTIVE TASKS count */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <FaClock className="text-blue-600" size={14} />
                  <span className="text-xs text-gray-500 uppercase">{translations[langMode].activeTasksUpperLabel}</span>
                </div>
                <p className="text-sm text-gray-900 font-medium">
                  {selectedLocation.tasks?.length || 0}{" "}
                  {selectedLocation.tasks?.length === 1 ? translations[langMode].taskWord : translations[langMode].tasksWord}
                </p>
              </div>

              {/* Task details list - inside Selected Location (Task ID, Lat/Long, Assignee, Status, Account No) */}
              {selectedLocation.tasks && selectedLocation.tasks.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 uppercase mb-2">
                    {selectedLocation.isDrawnArea
                      ? `${translations[langMode].tasksInDrawnAreaTitle} (${selectedLocation.tasks.length})`
                      : `${translations[langMode].tasksListTitle} (${selectedLocation.tasks.length})`}
                  </p>
                  <div className="space-y-3">
                    {selectedLocation.tasks.map((task, index) => {
                      const firstAssignee = task.assignees?.[0];
                      const assigneeName =
                        task.assignees?.length > 0
                          ? firstAssignee.name ||
                          firstAssignee.userName ||
                          firstAssignee.firstName ||
                          "?"
                          : primaryAgentName || translations[langMode].unassignedLabel;
                      const completedTaskCount = firstAssignee?.completedTaskCount;
                      const taskStatusDisplay =
                        task.taskStatus === "In_Progress" ? "In Progress" : task.taskStatus || "?";
                      const taskStatusUi =
                        taskStatusDisplay === "?"
                          ? "?"
                          : getTaskStatusUiLabel(langMode, taskStatusDisplay, taskStatusDisplay);

                      return (
                        <div
                          key={task.taskId || index}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200"
                        >
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 uppercase">{translations[langMode].taskIdLabel}</span>
                              <span className="font-medium text-gray-900">{task.taskId ?? "?"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaLocationDot className="text-blue-600 shrink-0" size={12} />
                              <span className="text-xs text-gray-500 uppercase">{translations[langMode].latLongUpperLabel}</span>
                              <span className="text-gray-900">
                                {task.latitude != null && task.longitude != null
                                  ? formatCoordinates(task.latitude, task.longitude)
                                  : "?"}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 uppercase">{translations[langMode].assigneeLabel}</span>
                              <span className="text-gray-900">
                                {assigneeName}
                                {task.assignees?.length > 0 && completedTaskCount != null && (
                                  <span className="text-gray-500 font-normal ml-1">
                                    ({translations[langMode].completedCountSuffix}: {completedTaskCount})
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 uppercase">{translations[langMode].taskStatusSection}</span>
                              <span
                                className="font-medium px-2 py-0.5 rounded text-xs"
                                style={{
                                  backgroundColor: `${getStatusColor(task.taskStatus)}20`,
                                  color: getStatusColor(task.taskStatus),
                                }}
                              >
                                {taskStatusUi}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 uppercase">{translations[langMode].accountNo}</span>
                              <span className="text-gray-900">{task.accountNo ?? "?"}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <FaLocationDot className="text-blue-600" size={32} />
              </div>
              <p className="text-sm text-gray-500 text-center">
                {translations[langMode].clickMarkerForLocationDetails}
              </p>
            </div>
          )}
        </div>

        {/* Selected Marker Task Details */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">{translations[langMode].taskDetailsSection}</h3>
            {selectedTaskDetail && (
              <ActionIconButton
                type="button"
                tone={canUpdateTask ? "blue" : "gray"}
                disabled={!canUpdateTask}
                ariaLabel="Edit task details"
                className={`h-9 w-9 border ${
                  canUpdateTask
                    ? "border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300"
                    : "border-gray-200 bg-gray-50 text-gray-300"
                }`}
                onClick={() => canUpdateTask && setShowTaskDetailEdit(true)}
              >
                <Pencil size={16} />
              </ActionIconButton>
            )}
          </div>

          {isTaskDetailLoading ? (
            <div className="text-sm text-gray-500">{translations[langMode].loadingTaskDetails}</div>
          ) : !selectedTaskDetail ? (
            <div className="text-sm text-gray-500">{translations[langMode].clickMarkerLoadTaskDetails}</div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase">{translations[langMode].taskIdLabel}</p>
                  <p className="text-gray-900 font-medium">{selectedTaskDetail.taskId || "?"}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase">{translations[langMode].accountNo}</p>
                  <p className="text-gray-900 font-medium">{selectedTaskDetail.accountNo || "?"}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase">Overview</p>
                  <span className="text-[11px] text-gray-400">Click Edit to update</span>
                </div>
                <div className="space-y-1.5">
                  <p className="text-gray-800">
                    <span className="text-xs text-gray-500 uppercase mr-2">Activity:</span>
                    <span className="font-medium">{selectedTaskDetail.taskDetailsParsed?.Activity || "?"}</span>
                  </p>
                  <p className="text-gray-800">
                    <span className="text-xs text-gray-500 uppercase mr-2">Status:</span>
                    <span className="font-medium">{selectedTaskDetail.taskStatus || "?"}</span>
                  </p>
                  <p className="text-gray-800">
                    <span className="text-xs text-gray-500 uppercase mr-2">Priority:</span>
                    <span className="font-medium">{selectedTaskDetail.priority || "?"}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Available Agents Section - Always visible */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FiUser className="text-blue-600" size={18} />
            <h3 className="text-base font-semibold text-gray-900">{translations[langMode].availableAgents}</h3>
          </div>
          {!loadingAgents && availableAgents.length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1 min-w-0">
                <IoSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder={translations[langMode].searchFieldAgentsPlaceholder}
                  value={agentSearchQuery}
                  onChange={(e) => setAgentSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-2 shrink-0" title={agentStatusFilter === "online" ? "Online" : "Offline"}>
                <FiUser
                  size={16}
                  className={agentStatusFilter === "online" ? "text-blue-500 shrink-0" : "text-gray-400 shrink-0"}
                  aria-hidden
                  title="Online"
                />
                <div className="select-none caret-transparent">
                  <input
                    id="detail-panel-agent-status-toggle"
                    type="checkbox"
                    role="switch"
                    aria-checked={agentStatusFilter === "online"}
                    aria-label={agentStatusFilter === "online" ? "Show online agents" : "Show offline agents"}
                    checked={agentStatusFilter === "online"}
                    onChange={() => setAgentStatusFilter((s) => (s === "online" ? "offline" : "online"))}
                    className="hidden"
                  />
                  <label
                    htmlFor="detail-panel-agent-status-toggle"
                    className={`relative inline-flex h-7 w-12 items-center p-0.5 flex-shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 select-none caret-transparent ${
                      agentStatusFilter === "online" ? "bg-blue-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                        agentStatusFilter === "online"
                          ? isToggleRtl
                            ? "-translate-x-5"
                            : "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </label>
                </div>
                <FaUserSlash
                  size={16}
                  className={agentStatusFilter === "offline" ? "text-blue-500 shrink-0" : "text-gray-400 shrink-0"}
                  aria-hidden
                  title="Offline"
                />
              </div>
            </div>
          )}
          {loadingAgents ? (
            <div className="text-center text-gray-500 py-4">{translations[langMode].loadingAgentsLabel}</div>
          ) : (() => {
            const q = agentSearchQuery.trim().toLowerCase();
            let filteredAgents = q
              ? availableAgents.filter((agent) => {
                const name = (agent.name || agent.userName || agent.firstName || "").toLowerCase();
                return name.includes(q);
              })
              : availableAgents;
            const wantOnline = agentStatusFilter === "online";
            filteredAgents = filteredAgents.filter((agent) => getAgentStatus(agent).isOnline === wantOnline);
            return filteredAgents.length > 0 ? (
              <div className="space-y-3">
                {filteredAgents.map((agent) => {
                  const agentName = agent.name || agent.userName || agent.firstName || "Unknown";
                  const initials = getInitials(agentName);
                  const agentId = agent.userId || agent.id;
                  const agentStatus = getAgentStatus(agent);

                  // Whether this agent is the active map filter (assigneeId)
                  const isFilterActive =
                    selectedAgentFilterId != null &&
                    selectedAgentFilterId !== "" &&
                    String(agentId) === String(selectedAgentFilterId);

                  // Check if this agent is on any of the selected location's tasks
                  const isOnSelectedTasks =
                    selectedLocation?.tasks?.some((task) =>
                      Array.isArray(task.assignees) &&
                      task.assignees.some((a) => {
                        const aId = a.userId || a.id;
                        const aName = (a.name || a.userName || a.firstName || "").toLowerCase();
                        return (
                          (aId && agentId && String(aId) === String(agentId)) ||
                          (aName && agentName && aName === agentName.toLowerCase())
                        );
                      })
                    ) || false;

                  // Check if this agent is selected (matches by ID or name, or appears on selected tasks)
                  const isSelected = isOnSelectedTasks
                    ? true
                    : selectedAgentId
                      ? agentId === selectedAgentId || String(agentId) === String(selectedAgentId)
                      : selectedAgentName
                        ? agentName.toLowerCase() === selectedAgentName.toLowerCase() ||
                        agent.name?.toLowerCase() === selectedAgentName.toLowerCase() ||
                        agent.userName?.toLowerCase() === selectedAgentName.toLowerCase() ||
                        agent.firstName?.toLowerCase() === selectedAgentName.toLowerCase()
                        : false;

                  return (
                    <div
                      key={agentId}
                      role="button"
                      tabIndex={0}
                      onClick={() => onAgentClick?.(agentId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onAgentClick?.(agentId);
                        }
                      }}
                      title={isFilterActive ? translations[langMode].clearFilter ?? "Clear filter" : translations[langMode].filterByAgent ?? "Filter tasks by this agent"}
                      className={`flex items-center gap-3 p-3 cursor-pointer rounded-lg border border-gray-200 hover:opacity-90 transition ${isFilterActive
                        ? "ring-2 ring-blue-400"
                        : isSelected
                          ? "border-2 border-blue-500 shadow-md"
                          : ""
                        }`}
                    >
                      {/* Avatar - rounded square to match image */}
                      <div className="relative">
                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white font-semibold text-sm">
                          {initials}
                        </div>
                        {/* Status dot: green = Online, gray = Office; blue if selected */}
                        <div
                          className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${isSelected ? "bg-blue-500" : agentStatus.isOnline ? "bg-green-500" : "bg-gray-400"
                            }`}
                          title={agentStatus.label}
                        />
                      </div>
                      {/* Agent Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{agentName}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-gray-500">
                            {agent.assignmentCount || 0} {translations[langMode].assignTaskCountVerb}{" "}
                            {agent.assignmentCount === 1 ? translations[langMode].taskSingular : translations[langMode].tasksPlural}
                          </span>
                        </div>
                      </div>
                      {/* Completed task count - right side */}
                      <div className="shrink-0 text-right">
                        <span className="text-xs font-medium text-gray-700">
                          {agent.completedTaskCount}
                        </span>
                        <p className="text-[10px] text-gray-500">{translations[langMode].completedCountSuffix}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : availableAgents.length > 0 ? (
              <div className="text-center text-gray-500 py-4 text-sm">{translations[langMode].noAgentsMatchSearch}</div>
            ) : (
              <div className="text-center text-gray-500 py-4 text-sm">{translations[langMode].noAgentsAvailable}</div>
            );
          })()}
        </div>

      </div>

      {showTaskDetailEdit && selectedTaskDetail && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={() => setShowTaskDetailEdit(false)}
            aria-hidden="true"
          />

          {/* Modal */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-detail-edit-title"
            className="relative w-full max-w-md max-h-[85vh] flex flex-col rounded-lg shadow-2xl bg-white border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-gray-100 rounded-t-lg">
              <div className="flex-1 pr-4">
                <h2 id="task-detail-edit-title" className="text-xl font-semibold tracking-tight text-gray-900">
                  Update Task Parameters
                </h2>
                <p className="text-xs text-gray-500 mt-1">Read-only fields are locked</p>
              </div>
              <ModalCloseButton onClick={() => setShowTaskDetailEdit(false)} ariaLabel="Close" />
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2 text-sm mb-1">
                <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase">{translations[langMode].taskIdLabel}</p>
                  <p className="text-gray-900 font-medium">{selectedTaskDetail.taskId || "?"}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase">{translations[langMode].accountNo}</p>
                  <p className="text-gray-900 font-medium">{selectedTaskDetail.accountNo || "?"}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <label className="text-xs font-semibold text-gray-700">Activity</label>
                <input
                  type="text"
                  name="Activity"
                  value={taskDetailFormData.Activity || ""}
                  disabled
                  className="w-full px-3 py-2 text-sm border rounded-md bg-gray-200 text-gray-600 cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-1 gap-2">
                <label className="text-xs font-semibold text-gray-700">Task Status</label>
                <input
                  type="text"
                  name="Status"
                  value={taskDetailFormData.Status || ""}
                  disabled
                  className="w-full px-3 py-2 text-sm border rounded-md bg-gray-200 text-gray-600 cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-1 gap-2">
                <label className="text-xs font-semibold text-gray-700">Priority</label>
                <input
                  type="text"
                  name="priority"
                  value={taskDetailFormData.priority || ""}
                  disabled
                  className="w-full px-3 py-2 text-sm border rounded-md bg-gray-200 text-gray-600 cursor-not-allowed"
                />
              </div>
              {Object.keys(taskDetailFormData)
                .filter((key) => key !== "Activity" && key !== "Status" && key !== "priority")
                .map((key) => (
                  <div className="grid grid-cols-1 gap-2" key={key}>
                    <label className="text-xs font-semibold text-gray-700">{key}</label>
                    <input
                      type="text"
                      name={key}
                      value={taskDetailFormData[key] || ""}
                      onChange={handleTaskDetailInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                    />
                  </div>
                ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100">
              <CommonButton colorIntent="danger"
                type="button"
                variant="danger"
                onClick={() => setShowTaskDetailEdit(false)}
              >
                Cancel
              </CommonButton>
              <CommonButton colorIntent="success"
                type="button"
                variant="primary"
                disabled={taskDetailSaving}
                onClick={handleUpdateTaskDetail}
              >
                {taskDetailSaving ? "Updating..." : "Update"}
              </CommonButton>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DetailPanel;
